import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Link,
  Spinner,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { CargoType as OldCargoType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  batteryTypeOptions,
  calculateDistance,
  cargoCoolingOptions,
  cargoCoolingRangeOptions,
  confirmationOptions,
  customerCargoTypeOptions,
  customerSubCargoTypeOptions,
  ensureMetricCargo,
  formatAddress,
  freightServiceAddOnOptions,
  freightServiceOptions,
  getCommoditiesFor,
  getGrossWeight,
  getVolumetricWeight,
  kgToLb,
  PREFERRED_ORIGIN_GATEWAYS,
  prettyNumber,
  Units,
} from '@pelicargo/utils';
import { CargoType, FreightService, HandlingOptions } from '@prisma/client';
import { AccountType, BatteryType, WeightUnit } from '@prisma/client';
import { sortBy } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { HiInformationCircle, HiOutlineInformationCircle } from 'react-icons/hi2';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useDebounce, useSessionStorage } from 'usehooks-ts';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../hooks/auth/useAuth';
import { defaultRequestFormValues, RequestFormValues, useRequestForm } from '../../hooks/request/useForm';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { API } from '../../utils/apiTypes';
import { cargoWarnings } from '../../utils/cargoWarnings';
import { CargoDetails } from '../CargoDetails';
import { CargoListItem } from '../CargoListItem';
import { SimpleDatePickerInput } from '../DatePicker/SimpleDatePickerInput';
import { LabeledValue } from '../LabeledValue';
import { DynamicRoutePicker } from '../RoutePicker/DynamicRoutePicker';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleCheckboxGroup } from '../SimpleCheckboxGroup';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleSwitch } from '../SimpleSwitch';
import { DangerousGoodsForm } from './DangerousGoodsForm';

const isCommodityRequired = (values: RequestFormValues, length: number, width: number, height: number, cw: number) => {
  return (
    length >= 318 || width >= 244 || height >= 162 || cw >= 2000 || values?.cargo_type !== OldCargoType.GeneralCargo
  );
};

type SimpleRequestFormProps = {
  isUpdate?: boolean;
  submitLabel?: string;
  onSubmit?: (request: Partial<RequestFormValues>) => void;
  showShipmentImporter?: boolean;
};

export const SimpleRequestForm = memo(
  ({ submitLabel = 'Search Deals', onSubmit, showShipmentImporter = false }: SimpleRequestFormProps) => {
    const { methods } = useRequestForm();
    const { reset, setValue } = methods;
    const { targetAccount } = useTargetAccount();
    const { isLoggedIn } = useAuth();
    const isUncertified = useMemo(() => targetAccount?.type === AccountType.UNCERTIFIED, [targetAccount?.type]);
    const [request] = useSessionStorage('request', {
      ...defaultRequestFormValues,
      freight_service: isUncertified ? FreightService.EXW : FreightService.AFR, // Default to AFR for certified accounts
    } as Partial<RequestFormValues>);

    const freightService = useWatch({
      control: methods.control,
      name: 'freight_service',
      defaultValue: request?.freight_service ?? FreightService.EXW,
    });

    const isExw = useMemo(() => freightService === FreightService.EXW, [freightService]);

    const [requestTextInput, setRequestText] = useState('');
    const requestText = useDebounce(requestTextInput, 500);
    const { mutate: ingestRequest, data, isLoading } = trpc.ingestRequest.useMutation();
    const [ingestedRequest, setIngestedRequest] = useState<typeof request>(request);

    useEffect(() => {
      if (requestText) ingestRequest({ requestText });
    }, [ingestRequest, requestText]);

    const handleIngestedRequest = useCallback(
      async (ingested) => {
        if (!ingested) return;
        let originAirports = ingested.origin_airports;
        const cargoOrigin = ingested?.cargo_origin;
        if (isExw && cargoOrigin?.zip_code) {
          const result = (await getGeocode({ address: formatAddress(cargoOrigin) }))?.[0];
          const { lat: latitude, lng: longitude } = getLatLng(result);
          const distances = PREFERRED_ORIGIN_GATEWAYS.map((gateway) => {
            const distance = calculateDistance(
              { latitude, longitude },
              { latitude: gateway.lat, longitude: gateway.lng },
            );
            return { id: gateway.id, distance };
          });
          const sortedDistances = sortBy(distances, 'distance');
          const closestGateway = sortedDistances[0];
          originAirports = [closestGateway.id];
        }

        const data = {
          ...defaultRequestFormValues,
          ...ingested,
          origin_airports: originAirports.slice(0, 4),
          destination_airports: ingested.destination_airports.slice(0, 4),
          freight_service: freightService,
          is_llm_generated: true,
          cargo:
            ingested?.cargo?.map((cargo) => {
              let handling = cargo?.handling?.[0];
              // Check to see if incomingHandling is typeof HandlingOptions
              if (!HandlingOptions?.[handling]) {
                handling = HandlingOptions.Stackable;
              }
              return {
                ...cargo,
                handling,
              };
            }) || [],
        };

        setIngestedRequest(data);
      },
      [freightService, isExw],
    );

    useEffect(() => {
      if (data) handleIngestedRequest(data);
    }, [data, handleIngestedRequest]);

    useEffect(() => {
      if (ingestedRequest) reset(ingestedRequest);
    }, [ingestedRequest, reset]);

    useEffect(() => {
      setValue('freight_service', isUncertified ? FreightService.EXW : FreightService.AFR);
    }, [isUncertified, setValue]);

    const cargo = useWatch({ control: methods.control, name: 'cargo' });
    const cargoType = useWatch({ control: methods.control, name: 'cargo_type' });
    const cargoTypeSub = useWatch({ control: methods.control, name: 'cargo_type_sub' });
    const commodityOptions = useMemo(() => getCommoditiesFor(cargoType, { cargoTypeSub }), [cargoType, cargoTypeSub]);
    const cargoDetails = useWatch({ control: methods.control, name: 'cargo_details' });
    const totalPieces = useWatch({ control: methods.control, name: 'total_pieces' });
    const grossWeight = useWatch({ control: methods.control, name: 'gross_weight' });
    const volumetricWeight = useWatch({ control: methods.control, name: 'volumetric_weight' });
    const dimensionUnit = useWatch({
      control: methods.control,
      name: 'original_dimension_unit',
      defaultValue: request?.original_dimension_unit,
    });
    const weightUnit = useWatch({
      control: methods.control,
      name: 'original_weight_unit',
      defaultValue: request?.original_weight_unit,
    });

    const isGrossWeight = cargoDetails === 'Gross Weight';

    const [warningMessage, setWarningMessage] = useState<string>('');

    const shipperTooltip = useDisclosure();

    const units = useMemo(() => ({ dimensionUnit, weightUnit }), [dimensionUnit, weightUnit]);

    const checkWarnings = (
      cargo: Omit<API.CargoItem, 'id' | 'created_at' | 'updated_at' | 'request_id'>[],
      units: Units,
      gw: number,
      vw: number,
    ) => {
      for (const cargoWarning of cargoWarnings) {
        const normalizedCargo =
          cargo?.map((c) => ({
            ...c,
            length: Number(c.length),
            width: Number(c.width),
            height: Number(c.height),
            quantity: Number(c.quantity),
          })) || [];

        if (cargoWarning.check(normalizedCargo, units, gw, vw)) {
          return cargoWarning.message;
        }
      }
      return '';
    };

    useEffect(() => {
      const handle = setTimeout(() => {
        // Ensure the cargo is in metric for our calculations
        const metricCargo = cargo?.map((c) => ensureMetricCargo(c, units));
        const weightShouldBeMetric = units.weightUnit === WeightUnit.kg;

        // Count the total number of pieces
        const totalPieces = cargo?.reduce((a, n) => a + Number(n.quantity), 0);
        methods.setValue('total_pieces', totalPieces);

        const mGw = metricCargo?.reduce((a, n) => a + getGrossWeight(n), 0);
        const gw = weightShouldBeMetric ? mGw : kgToLb(mGw);

        // Customer's can enter gross weight manually, or we can calculate it.
        // If GW is 0, we want to make sure the input has no value but shows the placeholder
        if (!isGrossWeight) methods.setValue('gross_weight', gw > 0 ? gw : null);

        // Calculate Volumetric Weight
        const mVw = metricCargo?.reduce((a, n) => a + getVolumetricWeight(n, { ceil: false }), 0);
        const vw = weightShouldBeMetric ? mVw : kgToLb(mVw);

        methods.setValue('volumetric_weight', vw);
        const warning = checkWarnings(cargo, units, gw, vw);
        setWarningMessage(warning);
      }, 250);

      // Cleanup on effect re-run or component unmount
      return () => clearTimeout(handle);
    }, [cargo, cargo.length, isGrossWeight, methods, units]);

    const resetFields = useCallback(
      (fields: string[]) => {
        fields.forEach((field: any) => setValue(field, ''));
      },
      [setValue],
    );

    const handleCargoTypeChange = () => {
      methods.setValue('cargo_type_sub', undefined);
      resetFields([
        'battery_type',
        'battery_type_variant',
        'un_number',
        'class',
        'packing_group',
        'aircraft_restriction',
        'packing_instruction',
        'section',
        'cargo_cooling_range',
        'cargo_cooling',
        'commodity',
      ]);
    };

    const handleBatteryTypeChange = (nextValue: BatteryType) => {
      if (nextValue === BatteryType.DG_BATTERIES_IN_EQUIPMENT) {
        resetFields(['battery_type_variant']);
        methods.setValue('commodity', 'DG Batteries');
      }
      if (nextValue === BatteryType.NON_RESTRICTED_BATTERIES_IN_EQUIPMENT) {
        resetFields(['un_number', 'class', 'packing_group', 'aircraft_restriction', 'packing_instruction', 'section']);
        methods.setValue('commodity', 'Non-Restricted Batteries in Equipment');
      }
    };

    const handleSubmit = useCallback(
      (values: RequestFormValues) => {
        // Validate the size of the shipment and confirm if the commodity is required

        const calculateDimensions = (cargo) =>
          cargo.reduce(
            (acc, c) => ({
              length: Math.max(acc.length, c.length),
              width: Math.max(acc.width, c.width),
              height: Math.max(acc.height, c.height),
              grossWeight: acc.grossWeight + getGrossWeight(c),
            }),
            { length: 0, width: 0, height: 0, grossWeight: 0 },
          );

        const inMetric = values?.cargo?.map((c) => ensureMetricCargo(c, units)) || [];
        const {
          length: greatestLength,
          width: greatestWidth,
          height: greatestHeight,
          grossWeight: calcGw,
        } = calculateDimensions(inMetric);

        const gw = isGrossWeight ? values?.gross_weight : calcGw;
        const cw = Math.max(values?.volumetric_weight, gw);

        const requiresCommodity = isCommodityRequired(values, greatestLength, greatestWidth, greatestHeight, cw);

        // Ensure commodity is set if required
        if (requiresCommodity && !values?.commodity) {
          const message = 'The actual commodity is required for this shipment.';
          return methods.setError('commodity', { message });
        }

        // If EXW, ensure zip code is set
        if (isExw && !values?.cargo_origin?.zip_code) {
          return methods.setError('origin_airports', {
            message: 'Please include a zip code in your pick up address.',
          });
        }

        onSubmit?.(values);
      },
      [isGrossWeight, isExw, onSubmit, units, methods],
    );

    return (
      <SimpleForm onSubmit={handleSubmit} {...(methods as UseFormReturn<any, any>)}>
        <Stack direction="row" spacing="16">
          <Stack flex="2">
            <Show if={isUncertified}>
              <Stack w="fit-content" spacing="4" overflowX="auto" maxW="full">
                <SimpleSwitch
                  variant="inline"
                  fieldVariant="inline"
                  name="freight_service"
                  options={freightServiceOptions.slice(1)} // Remove AFR
                />
                <HStack>
                  <SimpleCheckboxGroup
                    name="extra_freight_services"
                    options={freightServiceAddOnOptions}
                    direction="row"
                    spacing="4"
                  />
                  <SimpleCheckbox variant="unstyled" name="is_consolidated" colorScheme="blue" label="HAWB required" />
                </HStack>
              </Stack>
            </Show>

            <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(10, 1fr)' }} gap={4}>
              <DynamicRoutePicker isExw={isExw} />
              <GridItem colSpan={{ base: 1, lg: 4 }}>
                <SimpleDatePickerInput name="dropoff_by" label="Expected Drop Off" />
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(10, 1fr)' }} gap={4}>
              <GridItem colSpan={{ base: 1, lg: 6 }}>
                <Grid templateColumns={`repeat(2, 1fr)`} gap={4}>
                  <GridItem colSpan={1}>
                    <SimpleSelect
                      name="cargo_type"
                      label="Cargo Type"
                      placeholder="General Cargo"
                      options={customerCargoTypeOptions}
                      onChangeCallback={handleCargoTypeChange}
                    />
                  </GridItem>
                  <GridItem colSpan={1} position="relative" h="full">
                    <SimpleSwitch
                      name="is_known_shipper"
                      variant="outline"
                      label="Known Shipper"
                      options={confirmationOptions}
                    />
                    <Box position="absolute" right="1" top="1">
                      <Tooltip
                        hasArrow
                        placement="bottom"
                        bg="gray.800"
                        color="white"
                        isOpen={shipperTooltip.isOpen}
                        label={
                          <Stack>
                            <Text>Is the shipper a TSA Known Shipper? Link:</Text>
                            <Link
                              target="popup"
                              href="https://www.tsa.gov/for-industry/cargo-screening-program"
                              textDecoration="underline"
                            >
                              https://www.tsa.gov/for-industry/cargo-screening-program
                            </Link>
                          </Stack>
                        }
                      >
                        <IconButton
                          height="auto"
                          minW="auto"
                          aria-label="Toggle tooltip"
                          variant="unstyled"
                          icon={<HiInformationCircle />}
                          onMouseOver={shipperTooltip.onOpen}
                          onMouseLeave={shipperTooltip.onClose}
                          onClick={shipperTooltip.onToggle}
                        />
                      </Tooltip>
                    </Box>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 4 }}>
                <Show if={cargoType === OldCargoType.Batteries}>
                  <SimpleSelect
                    name="battery_type"
                    label="Battery Type"
                    placeholder="Battery Type"
                    options={batteryTypeOptions}
                    onChangeCallback={handleBatteryTypeChange}
                  />
                </Show>
                <Show if={cargoType !== OldCargoType.Batteries}>
                  <SimpleSelect name="commodity" label="commodity" placeholder="Commodity" options={commodityOptions} />
                </Show>
              </GridItem>
            </Grid>

            <Show if={cargoType === OldCargoType.Perishable}>
              <Grid templateColumns={{ base: 'repeat(3, 1fr)', lg: 'repeat(10, 1fr)' }} gap={4}>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <SimpleSelect
                    name="cargo_type_sub"
                    label="Sub Type"
                    placeholder="-"
                    options={customerSubCargoTypeOptions}
                  />
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <SimpleSelect
                    name="cargo_cooling_range"
                    label="Temperature Range"
                    options={cargoCoolingRangeOptions}
                  />
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 4 }}>
                  <SimpleSelect name="cargo_cooling" label="Cooling" options={cargoCoolingOptions} />
                </GridItem>
              </Grid>
            </Show>

            <Show if={cargoType === OldCargoType.DangerousGoods || cargoType === OldCargoType.Batteries}>
              <DangerousGoodsForm />
            </Show>

            <Heading variant="h6" zIndex={0}>
              Cargo Details
            </Heading>
            <CargoDetails />
            <SimpleFieldArray
              name="cargo"
              ignoreStyles
              spacing={4}
              isFormInModal={submitLabel === 'Update'}
              addButtonLabel="Add More Cargo"
              maxDisplayedItems={3}
              form={({ prefix }) => (
                <CargoListItem prefix={prefix} dimensionUnit={dimensionUnit} weightUnit={weightUnit} />
              )}
            />

            <Show if={!!warningMessage}>
              <Alert status="warning" variant="left-accent">
                <AlertIcon />
                <AlertDescription>{warningMessage}</AlertDescription>
              </Alert>
            </Show>

            <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(25, 1fr)' }} gap={4} pt="4">
              <GridItem colSpan={{ base: 1, lg: 5 }}>
                <LabeledValue label="Total Pieces" value={totalPieces || '-'} />
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 6 }}>
                <Show if={isGrossWeight}>
                  <SimpleInput
                    name="gross_weight"
                    placeholder="Gross Weight"
                    label={`Gross Weight (${weightUnit})`}
                    inputProps={{
                      autoComplete: 'off',
                    }}
                  />
                </Show>
                <Show if={!isGrossWeight}>
                  <LabeledValue label={`Gross Weight (${weightUnit})`} value={prettyNumber(grossWeight || 0) || '-'} />
                </Show>
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 6 }}>
                <LabeledValue
                  label={`Vol. Weight (${weightUnit})`}
                  value={prettyNumber(volumetricWeight || 0) || '-'}
                />
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 8 }}>
                <Button type="submit" colorScheme="primary" size="xl" w="full">
                  {submitLabel}
                </Button>
              </GridItem>
            </Grid>
          </Stack>
          <Show if={isLoggedIn && showShipmentImporter}>
            <Box w="full" h="auto" maxH="3xl" flex="1" position="relative" display={{ base: 'none', md: 'unset' }}>
              <Flex
                direction="column"
                h="full"
                bg="white"
                borderWidth={1}
                borderColor="border.default"
                borderRadius="md"
                p="4"
              >
                <Stack direction="row" justify="space-between">
                  <FormLabel textTransform="uppercase" fontSize="xs" fontWeight="600" mb="1">
                    Shipment Importer
                  </FormLabel>
                  <Tooltip
                    label="You are responsible for the details that you submit. Always double-check that information is correct before proceeding."
                    placement="top"
                  >
                    <Box>
                      <Icon as={HiOutlineInformationCircle} color="gray.500" />
                    </Box>
                  </Tooltip>
                </Stack>
                <Textarea
                  h="full"
                  variant="unstyled"
                  placeholder="Paste your shipment details here to pre-fill with Pelicargo AI"
                  onChange={(event) => setRequestText(event?.target?.value)}
                />
              </Flex>
              <Show if={isLoading}>
                <Stack
                  position="absolute"
                  w="full"
                  h="full"
                  top="0"
                  left="0"
                  zIndex="3"
                  justify="center"
                  align="center"
                  bg="gray"
                  opacity="0.5"
                  borderRadius="md"
                >
                  <Spinner />
                </Stack>
              </Show>
            </Box>
          </Show>
        </Stack>
      </SimpleForm>
    );
  },
);
